import { Building2, Loader2, Plus, User, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import type { CreateCaseRequest, InitialEntity } from '@/services/api'

export interface CreateCaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSubmit: (input: CreateCaseRequest) => void
  creating: boolean
  /** Server-side error from the create mutation, if any. */
  error?: string | null
}

export function CreateCaseDialog({
  open,
  onOpenChange,
  onSubmit,
  creating,
  error,
}: CreateCaseDialogProps) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [localError, setLocalError] = useState<string | null>(null)
  const [initialEntities, setInitialEntities] = useState<InitialEntity[]>([])

  const reset = () => {
    setName('')
    setDescription('')
    setInitialEntities([])
    setLocalError(null)
  }

  const addEntity = () =>
    setInitialEntities([...initialEntities, { name: '', entity_type: 'Person', is_suspect: true }])
  const removeEntity = (index: number) =>
    setInitialEntities(initialEntities.filter((_, i) => i !== index))
  const updateEntity = (index: number, field: keyof InitialEntity, value: string | boolean) =>
    setInitialEntities(initialEntities.map((e, i) => (i === index ? { ...e, [field]: value } : e)))

  const handleSubmit = () => {
    if (!name.trim()) {
      setLocalError(t('cases.create.nameRequired'))
      return
    }
    setLocalError(null)
    const validEntities = initialEntities.filter(e => e.name.trim())
    onSubmit({
      name: name.trim(),
      description: description.trim() || undefined,
      initial_entities: validEntities.length > 0 ? validEntities : undefined,
    })
  }

  return (
    <Dialog
      open={open}
      onOpenChange={isOpen => {
        if (!isOpen) reset()
        onOpenChange(isOpen)
      }}
    >
      <DialogContent className="bg-card border-border max-w-lg overflow-y-auto md:max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="text-foreground">{t('cases.create.title')}</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {t('cases.create.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-3">
            <div className="flex items-center justify-between max-md:gap-3">
              <Label className="text-foreground max-md:leading-snug">
                {t('cases.create.initialEntities')}
              </Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addEntity}
                className="border-border text-foreground hover:bg-accent h-9 px-2 md:h-7"
              >
                <Plus className="h-3 w-3 mr-1" />
                {t('cases.create.addEntity')}
              </Button>
            </div>

            {initialEntities.length > 0 ? (
              <div className="space-y-2">
                {initialEntities.map((entity, index) => (
                  <div
                    // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional, no stable id
                    key={index}
                    className="flex items-start gap-2 p-3 rounded-lg bg-muted/50 border border-border"
                  >
                    <div className="flex-1 space-y-2">
                      <Input
                        placeholder={t('cases.create.entityNamePlaceholder')}
                        value={entity.name}
                        onChange={e => updateEntity(index, 'name', e.target.value)}
                        className="bg-muted border-border text-foreground placeholder:text-muted-foreground md:h-8"
                      />
                      <div className="flex items-center gap-3">
                        <Select
                          value={entity.entity_type}
                          onValueChange={(value: 'Person' | 'Company') =>
                            updateEntity(index, 'entity_type', value)
                          }
                        >
                          <SelectTrigger className="w-32 h-8 bg-muted border-border text-foreground max-md:h-9!">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="bg-muted border-border">
                            <SelectItem value="Person" className="text-foreground hover:bg-accent">
                              <span className="flex items-center gap-2">
                                <User className="h-3 w-3" />
                                {t('cases.create.person')}
                              </span>
                            </SelectItem>
                            <SelectItem value="Company" className="text-foreground hover:bg-accent">
                              <span className="flex items-center gap-2">
                                <Building2 className="h-3 w-3" />
                                {t('cases.create.company')}
                              </span>
                            </SelectItem>
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id={`suspect-${index}`}
                            checked={entity.is_suspect}
                            onCheckedChange={checked =>
                              updateEntity(index, 'is_suspect', !!checked)
                            }
                            className="border-border data-[state=checked]:bg-destructive data-[state=checked]:border-destructive"
                          />
                          <Label
                            htmlFor={`suspect-${index}`}
                            className="text-xs text-muted-foreground cursor-pointer"
                          >
                            {t('cases.create.suspect')}
                          </Label>
                        </div>
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeEntity(index)}
                      className="size-9 p-0 text-muted-foreground hover:text-destructive hover:bg-accent md:size-8"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">{t('cases.create.noEntitiesHint')}</p>
            )}
          </div>

          <div className="border-t border-border pt-4" />

          <div className="space-y-2">
            <Label htmlFor="name" className="text-foreground">
              {t('cases.create.caseName')}
            </Label>
            <Input
              id="name"
              placeholder={t('cases.create.caseNamePlaceholder')}
              value={name}
              onChange={e => setName(e.target.value)}
              className="bg-muted border-border text-foreground placeholder:text-muted-foreground"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="description" className="text-foreground">
              {t('cases.create.descriptionOptional')}
            </Label>
            <Textarea
              id="description"
              placeholder={t('cases.create.descriptionPlaceholder')}
              value={description}
              onChange={e => setDescription(e.target.value)}
              className="bg-muted border-border text-foreground placeholder:text-muted-foreground min-h-[80px]"
            />
          </div>

          {(localError || error) && (
            <p className="text-sm text-destructive">{localError || error}</p>
          )}
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-border text-foreground hover:bg-accent"
          >
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={creating}
            className="bg-primary text-primary-foreground hover:bg-primary/90"
          >
            {creating && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {creating ? t('cases.create.creating') : t('cases.create.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
